import { breakpointMap } from "@theme/themes/ms5/theme";
import { useEffect, useState } from "react";

export type BreakpointKey = keyof typeof breakpointMap;

/**
 * useMediaQuery
 *
 * A React hook for evaluating CSS media queries and observing breakpoint matches.
 *
 * @param query - A breakpoint key (e.g., "md", "lg") or a numeric pixel value (e.g., 900)
 * @param type  - The media query type, either "min" or "max" for min-width/max-width (defaults to "min")
 * @returns     - Boolean state indicating if the media query currently matches
 *
 * ## Usage
 * useMediaQuery("md")              → (min-width: 768px)
 * useMediaQuery("sm", "max")       → (max-width: 480px)
 * useMediaQuery(900)               → (min-width: 900px)
 * useMediaQuery(1024, "max")       → (max-width: 1024px)
 */
export const useMediaQuery = (query: BreakpointKey | number, type: "min" | "max" = "min") => {
  const value = typeof query === "number" ? `${query}px` : breakpointMap[query];
  const mediaQuery = `(${type}-width: ${value})`;

  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;

    return window.matchMedia(mediaQuery).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(mediaQuery);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mql.matches);
    mql.addEventListener("change", handleChange);

    return () => mql.removeEventListener("change", handleChange);
  }, [mediaQuery]);

  return matches;
};
